import { validator } from "@/utils/validator";
import { useMemo } from "react";
import {
  MAX_CHARACTER_INPUT_VALIDATION,
  MAX_COMPANY_ADDRESS_INPUT_VALIDATION,
  MAX_COMPANY_INPUT_VALIDATION,
  MAX_REPRESENTATIVE_NAME_INPUT_VALIDATION,
  MAX_TEXT_INPUT_VALIDATION,
  MAX_YEAR_INPUT_VALIDATION,
  MAXIMUM_NUMBER_VALIDATION,
} from "@/constants/common";
import { useBusinessOfficeSchema } from "@/features/company/hooks/useBusinessOfficeSchema";
import { useCompanyBranchSchema } from "@/features/company/hooks/useCompanyBranchSchema";
import { useContractFileSchema } from "@/features/company/hooks/useContractFileSchema";
import { string } from "yup";
import { PATTERN } from "@/constants/pattern";

export const useGetCompanySchema = () => {
  const { businessOfficeSchema } = useBusinessOfficeSchema();
  const { companyBranchSchema } = useCompanyBranchSchema();
  const { contractFileSchema } = useContractFileSchema();

  const companySchema = useMemo(() => {
    return validator.object().shape({
      companyName: validator.string().emptyToNull().autoTransformToFullWidth().trim().label("企業名").required().max(MAX_COMPANY_INPUT_VALIDATION),
      companyKanaName: validator
        .string()
        .trim()
        .katakanaToHiragana()
        .hiraganaOnly()
        .emptyToNull()
        .label("企業名かな")
        .nullable()
        .max(MAX_CHARACTER_INPUT_VALIDATION),
      mainIndustryId: validator.number().isSelection().emptyToNull().optional().label("業種").nullable(),
      establishmentYear: validator
        .string()
        .trim()
        .autoTransformToHalfWidth()
        .numbersOnly()
        .emptyToNull()
        .label("設立年")
        .max(MAX_YEAR_INPUT_VALIDATION)
        .test("is-between-1900-and-current-year", "設立年は1900年から今年までの範囲で入力してください。", (value) => {
          if (!value) return true;
          const year = Number(value);
          const currentYear = new Date().getFullYear();
          return year >= 1900 && year <= currentYear;
        })
        .nullable(),
      numberOfEmployees: validator.string().trim().emptyToNull().label("従業員数").max(MAX_CHARACTER_INPUT_VALIDATION).nullable(),
      capital: validator
        .string()
        .emptyToNull()
        .label("資本金")
        .nullable()
        .test("is-only-number", "「資本金」は数字で入力してください。", function (value) {
          if (!value) return true;
          return /^(?:\d+|\d{1,3}(?:,\d{3})*)$/.test(value);
        })
        .test("is-max", `「資本金」は ${MAXIMUM_NUMBER_VALIDATION} 以下で入力してください。`, function (value) {
          if (!value) return true;
          const string = value.replaceAll(",", "");
          return Number(string) <= MAXIMUM_NUMBER_VALIDATION;
        }),
      representativeName: validator
        .string()
        .trim()
        .nameOnly()
        .emptyToNull()
        .label("代表者名")
        .max(MAX_REPRESENTATIVE_NAME_INPUT_VALIDATION)
        .nullable()
        .test("is-valid-name", "「名前」は日本語または英字のみ入力できます。", function (value) {
          if (!value) return true;
          return /^[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\u3400-\u4DBF\u3005 A-Za-zＡ-Ｚａ-ｚ\s]+$/.test(value);
        })
        .autoTransformToFullWidth(),
      representativePosition: validator
        .string()
        .trim()
        .emptyToNull()
        .label("代表者役職")
        .max(MAX_REPRESENTATIVE_NAME_INPUT_VALIDATION)
        .nullable()
        .autoTransformToFullWidth(),
      telNumber: validator
        .string()
        .trim()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .isTelNumber()
        .label("電話番号")
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .nullable(),
      faxNumber: validator
        .string()
        .trim()
        .autoTransformToHalfWidth()
        .emptyToNull()
        .isTelNumber("FAX番号の形式が正しくありません。例：03-1234-5678 の形式で入力してください。")
        .label("FAX番号")
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .nullable(),
      contactEmail: validator
        .string()
        .trim()
        .emptyToNull()
        .test("is-email", "正しいメールアドレス形式で入力してください。", (value) => {
          if (!value) return true;
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        })
        .label("メールアドレス")
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .nullable(),
      websiteUrl: validator
        .string()
        .trim()
        .emptyToNull()
        .matches(PATTERN.CUSTOM_URL, "正しいURL形式で入力してください。")
        .label("ウェブサイトURL")
        .max(MAX_TEXT_INPUT_VALIDATION)
        .nullable(),
      zipCode: validator.string().emptyToNull().zipCode().autoTransformToHalfWidth().label("郵便番号").max(MAX_CHARACTER_INPUT_VALIDATION).nullable(),
      prefecture: validator.string().trim().emptyToNull().label("都道府県").nullable(),
      city: validator.string().trim().emptyToNull().label("市区町村").nullable(),
      address: validator
        .string()
        .trim()
        .emptyToNull()
        .autoTransformToFullWidth()
        .label("町名丁目番地号等")
        .max(MAX_COMPANY_ADDRESS_INPUT_VALIDATION)
        .nullable(),
      businessOffices: validator.array().of(businessOfficeSchema).label("businessOffices").nullable(),
      companyBranches: validator.array().of(companyBranchSchema).label("companyBranches").nullable(),
      companyDescription: validator.string().emptyToNull().label("事業内容・企業特性").max(MAX_TEXT_INPUT_VALIDATION).nullable(),
      memo: validator.string().emptyToNull().label("メモ").max(MAX_TEXT_INPUT_VALIDATION).nullable(),
      managementNumber: validator.string().trim().removeSpaces().emptyToNull().label("企業管理番号").max(MAX_CHARACTER_INPUT_VALIDATION).nullable(),
      contractPaths: validator.array().of(string().emptyToNull().required()).label("contractPaths").nullable(),
      contractFiles: validator.array().of(contractFileSchema).label("contractFiles").nullable(),
      contractUrl: validator
        .string()
        .trim()
        .emptyToNull()
        .matches(PATTERN.CUSTOM_URL, "正しいURL形式で入力してください。")
        .label("契約書URL")
        .max(MAX_TEXT_INPUT_VALIDATION)
        .nullable(),
      companyLogoFile: validator.mixed<File>().nullable(),
      companyLogo: validator.string().nullable(),
      contactPersonName: validator
        .string()
        .trim()
        .nameOnly()
        .emptyToNull()
        .label("名前")
        .max(MAX_REPRESENTATIVE_NAME_INPUT_VALIDATION)
        .nullable()
        .test("is-valid-name", "「名前」は日本語または英字のみ入力できます。", function (value) {
          if (!value) return true;
          return /^[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\u3400-\u4DBF\u3005 A-Za-z\s]+$/.test(value);
        }),
      contactPersonEmail: validator
        .string()
        .trim()
        .emptyToNull()
        .test("is-email", "正しいメールアドレス形式で入力してください。", (value) => {
          if (!value) return true;
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        })
        .label("メールアドレス")
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .nullable(),
      contactPersonPhone: validator
        .string()
        .trim()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .isTelNumber()
        .label("電話番号")
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .nullable(),
      contactPersonJobTitle: validator
        .string()
        .trim()
        .emptyToNull()
        .label("役職")
        .max(MAX_REPRESENTATIVE_NAME_INPUT_VALIDATION)
        .autoTransformToFullWidth()
        .nullable(),
    });
  }, []);

  return { companySchema };
};
